/**
 * Pure math + format utilities for the PDF measurement layer.
 *
 * Coordinate space conventions:
 *   - "norm"    : 0–100 across the page (matches MarkupLayer / AnnotationLayer)
 *   - "pdf"     : PDF user units (1 unit = 1/72 inch). Use page natural width/height to convert.
 *   - "real"    : Real-world distance in the calibration's stored unit.
 *
 * `realPerPdfPoint` is the multiplier used to convert pdf-space distance to
 * the calibration's real-world unit. e.g. for 1/4" = 1' (architectural):
 *   1 PDF point = 1/72 inch on the printed sheet
 *   the printed sheet is at scale 1/4" = 1' so 1 inch on page = 4 ft real
 *   → realPerPdfPoint = (1/72) * 4  ft/pt = 0.05555… ft/pt.
 */

import type { PdfMeasurementCalibration } from '../types';

export type LengthUnit = 'in' | 'ft' | 'mm' | 'cm' | 'm';
export type DisplayUnit = 'in' | 'ft' | 'ft-in' | 'mm' | 'cm' | 'm';
export type UnitSystem = 'imperial' | 'metric';
export type AreaUnit = 'sqin' | 'sqft' | 'sqmm' | 'sqcm' | 'sqm';

const PT_PER_IN = 72;
const MM_PER_IN = 25.4;

export interface NormPoint {
  x: number;
  y: number;
}
export interface PdfPoint {
  x: number;
  y: number;
}

export interface StandardScale {
  id: string;
  label: string;
  group: 'Architectural' | 'Engineering' | 'Metric';
  unitSystem: UnitSystem;
  /** Displayed as the natural unit for this scale (e.g. ft for arch, mm for metric). */
  baseUnit: LengthUnit;
  defaultDisplayUnit: DisplayUnit;
  /** Multiplier from PDF points → baseUnit. */
  realPerPdfPoint: number;
}

/**
 * Architectural: scale "1/4\" = 1'" means every 1/4 inch on the printed plan
 * represents 1 foot in the real world. A 1/4 inch = 18 PDF points (72/4). So
 * realPerPdfPoint = 1 ft / 18 pt ≈ 0.05555 ft/pt.
 *
 * Engineering: "1\" = 20'" means every 1 inch on the plan = 20 ft real. 1 in
 * = 72 pt, so realPerPdfPoint = 20 ft / 72 pt ≈ 0.27778 ft/pt.
 *
 * Metric ratio "1:100" means every 1 unit on the plan = 100 units real, so
 * 1 mm on page = 100 mm real. There are 72/25.4 ≈ 2.8346 pt per mm, so
 * realPerPdfPoint (mm) = 100 / (72/25.4) = 100 * 25.4 / 72 ≈ 35.28 mm/pt.
 */
function archScale(label: string, denomFt: number): StandardScale {
  const ptPerInch = PT_PER_IN;
  const inchesOnPagePerFoot = 1 / denomFt; // e.g. 1/4 inch per foot for 1/4"=1'
  const pointsOnPagePerFoot = inchesOnPagePerFoot * ptPerInch;
  return {
    id: `arch-${label.replace(/[^a-zA-Z0-9]/g, '')}`,
    label: `${label} = 1'`,
    group: 'Architectural',
    unitSystem: 'imperial',
    baseUnit: 'ft',
    defaultDisplayUnit: 'ft-in',
    realPerPdfPoint: 1 / pointsOnPagePerFoot,
  };
}

function engScale(ftPerInch: number): StandardScale {
  return {
    id: `eng-1in-${ftPerInch}ft`,
    label: `1" = ${ftPerInch}'`,
    group: 'Engineering',
    unitSystem: 'imperial',
    baseUnit: 'ft',
    defaultDisplayUnit: 'ft-in',
    realPerPdfPoint: ftPerInch / PT_PER_IN,
  };
}

function metricScale(ratio: number): StandardScale {
  const mmPerPt = MM_PER_IN / PT_PER_IN; // ≈ 0.3528 mm per pt
  return {
    id: `metric-1-${ratio}`,
    label: `1:${ratio}`,
    group: 'Metric',
    unitSystem: 'metric',
    baseUnit: 'mm',
    defaultDisplayUnit: ratio >= 200 ? 'm' : 'mm',
    realPerPdfPoint: mmPerPt * ratio,
  };
}

export const STANDARD_SCALES: StandardScale[] = [
  archScale('1/16"', 16),
  archScale('1/8"', 8),
  archScale('3/32"', 32 / 3),
  archScale('3/16"', 16 / 3),
  archScale('1/4"', 4),
  archScale('3/8"', 8 / 3),
  archScale('1/2"', 2),
  archScale('3/4"', 4 / 3),
  archScale('1"', 1),
  archScale('1 1/2"', 2 / 3),
  archScale('3"', 1 / 3),
  engScale(10),
  engScale(20),
  engScale(30),
  engScale(40),
  engScale(50),
  engScale(60),
  engScale(100),
  engScale(200),
  metricScale(20),
  metricScale(50),
  metricScale(100),
  metricScale(200),
  metricScale(500),
  metricScale(1000),
];

export function getStandardScaleById(id: string): StandardScale | null {
  return STANDARD_SCALES.find(s => s.id === id) || null;
}

export function isMetricLengthUnit(unit: LengthUnit): boolean {
  return unit === 'mm' || unit === 'cm' || unit === 'm';
}

const IMPERIAL_DISPLAY_UNITS: DisplayUnit[] = ['ft-in', 'ft', 'in'];
const METRIC_DISPLAY_UNITS: DisplayUnit[] = ['mm', 'cm', 'm'];

/**
 * Keep `displayUnit` in the same unit system as the two-point real-distance unit.
 * Without this, a leftover metric displayUnit (e.g. from auto-detect or browsing a
 * metric standard scale) can be saved while the user entered ft/in — labels then
 * render in meters after refresh.
 */
export function coerceDisplayUnitForLengthUnit(
  realDistanceUnit: LengthUnit,
  displayUnit: DisplayUnit | null | undefined,
): DisplayUnit {
  const metric = isMetricLengthUnit(realDistanceUnit);
  const allowed = metric ? METRIC_DISPLAY_UNITS : IMPERIAL_DISPLAY_UNITS;
  if (displayUnit && allowed.includes(displayUnit)) return displayUnit;
  if (!metric) {
    if (realDistanceUnit === 'in') return 'in';
    return 'ft-in';
  }
  if (realDistanceUnit === 'mm' || realDistanceUnit === 'cm' || realDistanceUnit === 'm') {
    return realDistanceUnit;
  }
  return 'm';
}

/** Preferred label unit when the user just picked a two-point real-distance unit. */
export function preferredDisplayUnitForLengthUnit(realDistanceUnit: LengthUnit): DisplayUnit {
  switch (realDistanceUnit) {
    case 'in':
      return 'in';
    case 'ft':
      return 'ft-in';
    case 'mm':
      return 'mm';
    case 'cm':
      return 'cm';
    case 'm':
      return 'm';
  }
}

/* ------------------------------------------------------------------ */
/* Coordinate conversion                                               */
/* ------------------------------------------------------------------ */

/** Convert a normalized 0-100 point to PDF user units. */
export function normToPdf(p: NormPoint, pageW: number, pageH: number): PdfPoint {
  return {
    x: (p.x / 100) * pageW,
    y: (p.y / 100) * pageH,
  };
}

/* ------------------------------------------------------------------ */
/* Geometry                                                            */
/* ------------------------------------------------------------------ */

/** Length of a segment in the same units as the inputs. */
export function segmentLength(a: PdfPoint, b: PdfPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Sum of segment lengths along a polyline. */
export function polylineLength(points: PdfPoint[]): number {
  if (points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += segmentLength(points[i - 1], points[i]);
  }
  return total;
}

/** Polygon area using the shoelace formula. Result is always positive. */
export function polygonArea(points: PdfPoint[]): number {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

/** Angle in degrees at `vertex` between rays to `a` and `b`. */
export function angleAtVertex(a: PdfPoint, vertex: PdfPoint, b: PdfPoint): number {
  const v1x = a.x - vertex.x;
  const v1y = a.y - vertex.y;
  const v2x = b.x - vertex.x;
  const v2y = b.y - vertex.y;
  const dot = v1x * v2x + v1y * v2y;
  const m1 = Math.hypot(v1x, v1y);
  const m2 = Math.hypot(v2x, v2y);
  if (m1 < 1e-9 || m2 < 1e-9) return 0;
  let cos = dot / (m1 * m2);
  cos = Math.max(-1, Math.min(1, cos));
  return (Math.acos(cos) * 180) / Math.PI;
}

/* ------------------------------------------------------------------ */
/* Calibration application                                             */
/* ------------------------------------------------------------------ */

export function applyCalibration(pdfDistance: number, calibration: PdfMeasurementCalibration): number {
  return pdfDistance * calibration.realPerPdfPoint;
}

/** Convert a value in `from` to `to`. Both units must be in the same system. */
export function convertLength(value: number, from: LengthUnit, to: LengthUnit): number {
  if (from === to) return value;
  // Normalize to inches first (both systems have an in/mm bridge).
  const inInches = (() => {
    switch (from) {
      case 'in': return value;
      case 'ft': return value * 12;
      case 'mm': return value / MM_PER_IN;
      case 'cm': return (value * 10) / MM_PER_IN;
      case 'm':  return (value * 1000) / MM_PER_IN;
    }
  })();
  switch (to) {
    case 'in': return inInches;
    case 'ft': return inInches / 12;
    case 'mm': return inInches * MM_PER_IN;
    case 'cm': return (inInches * MM_PER_IN) / 10;
    case 'm':  return (inInches * MM_PER_IN) / 1000;
  }
}

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

function formatFtIn(valueInBase: number, baseUnit: LengthUnit): string {
  const inches = convertLength(valueInBase, baseUnit, 'in');
  const sign = inches < 0 ? '-' : '';
  const abs = Math.abs(inches);
  const ft = Math.floor(abs / 12);
  const remIn = abs - ft * 12;
  // Round inches to nearest 1/16 if small, else to nearest 1/4.
  const rounded = Math.round(remIn * 16) / 16;
  if (ft === 0) {
    return `${sign}${formatInchesFraction(rounded)}"`;
  }
  if (rounded === 0) {
    return `${sign}${ft}'`;
  }
  return `${sign}${ft}'-${formatInchesFraction(rounded)}"`;
}

function formatInchesFraction(inches: number): string {
  if (inches === 0) return '0';
  const whole = Math.floor(inches);
  const frac = inches - whole;
  if (frac < 1 / 32) return `${whole}`;
  // 16ths
  const sixteenths = Math.round(frac * 16);
  if (sixteenths === 16) return `${whole + 1}`;
  // Reduce fraction
  let num = sixteenths;
  let den = 16;
  while (num % 2 === 0 && den % 2 === 0) {
    num /= 2;
    den /= 2;
  }
  return whole > 0 ? `${whole} ${num}/${den}` : `${num}/${den}`;
}

export function formatLength(
  valueInBase: number,
  baseUnit: LengthUnit,
  displayUnit: DisplayUnit,
  precision = 2,
): string {
  if (displayUnit === 'ft-in') {
    return formatFtIn(valueInBase, baseUnit);
  }
  const targetUnit: LengthUnit = displayUnit;
  const v = convertLength(valueInBase, baseUnit, targetUnit);
  const fixed = Number.isFinite(v) ? v.toFixed(precision).replace(/\.?0+$/, '') : '0';
  const suffix: Record<LengthUnit, string> = { in: '"', ft: "'", mm: ' mm', cm: ' cm', m: ' m' };
  return `${fixed}${suffix[targetUnit]}`;
}

export function formatArea(
  pdfArea: number,
  calibration: PdfMeasurementCalibration,
): string {
  // Area scales by realPerPdfPoint². Express in the calibration's natural area unit.
  const realArea = pdfArea * calibration.realPerPdfPoint * calibration.realPerPdfPoint;
  const baseUnit = calibration.baseUnit;
  if (baseUnit === 'ft') {
    return `${realArea.toFixed(realArea < 10 ? 2 : 1)} sq ft`;
  }
  if (baseUnit === 'in') {
    return `${realArea.toFixed(2)} sq in`;
  }
  if (baseUnit === 'mm') {
    if (realArea > 1_000_000) return `${(realArea / 1_000_000).toFixed(2)} m²`;
    if (realArea > 10_000) return `${(realArea / 10_000).toFixed(2)} cm²`;
    return `${realArea.toFixed(0)} mm²`;
  }
  if (baseUnit === 'cm') return `${realArea.toFixed(1)} cm²`;
  if (baseUnit === 'm') return `${realArea.toFixed(2)} m²`;
  return `${realArea.toFixed(2)}`;
}

export function formatAngle(degrees: number): string {
  return `${degrees.toFixed(1)}°`;
}

/**
 * Convert an angle (degrees) to roof pitch expressed as rise per 12" run.
 *
 *   45°  → "12/12"
 *   ~26.57° → "6/12"
 *   90°  → "vertical"
 *   0°   → "0/12"
 *
 * Handles reflex angles (e.g. 135° from a 3-click measurement) by reducing
 * to the supplementary angle, which is what a roofer actually means by
 * "the pitch of that roof line."
 */
export function formatPitch(degrees: number): string {
  let a = Math.abs(degrees);
  if (a > 90) a = 180 - a;
  if (a >= 89.5) return 'vertical';
  if (a <= 0.25) return '0/12';
  const rise = Math.tan((a * Math.PI) / 180) * 12;
  // Sub-1/12 → one decimal so very low slopes still read sensibly.
  if (rise < 1) return `${rise.toFixed(1)}/12`;
  // Round to the nearest 1/2 — that covers every common roof pitch
  // (3/12, 4/12, 4.5/12, 6/12, 8/12, 9/12, 10/12, 12/12, 18/12 …).
  const rounded = Math.round(rise * 2) / 2;
  return Number.isInteger(rounded) ? `${rounded}/12` : `${rounded.toFixed(1)}/12`;
}

/** Convenience: format a length given pdf-space distance + calibration. */
export function formatPdfDistance(
  pdfDistance: number,
  calibration: PdfMeasurementCalibration,
  displayUnitOverride?: DisplayUnit,
): string {
  const real = applyCalibration(pdfDistance, calibration);
  return formatLength(real, calibration.baseUnit, displayUnitOverride || calibration.displayUnit);
}

/** Resolve a `realPerPdfPoint` value from a two-point calibration entry. */
export function resolveTwoPointCalibration(args: {
  pointA: NormPoint;
  pointB: NormPoint;
  pageW: number;
  pageH: number;
  realDistance: number;
  realUnit: LengthUnit;
  /** What unit to store calibration in (also used for display defaults). */
  baseUnit: LengthUnit;
}): number {
  const a = normToPdf(args.pointA, args.pageW, args.pageH);
  const b = normToPdf(args.pointB, args.pageW, args.pageH);
  const dPdf = segmentLength(a, b);
  if (dPdf < 1e-6) return 0;
  const realInBase = convertLength(args.realDistance, args.realUnit, args.baseUnit);
  return realInBase / dPdf;
}
